import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetQaSummaryQuery } from '@/redux/internal-services/jobsApi';
import { CardSection } from '@/components/UI/CardSection';

/** JOB-FEAT-05 — QA summary. Reads the real workflow_transition_history (the latest of
 *  inspect_job=pass / fail_qa_rework=fail — same source the complete_job gate already reads,
 *  see api-service app/domain/workflows/service.py's _validate_job_qa_passed) plus the
 *  qa_checklist task's status, so a PM/Owner can see the QA result and who recorded it without
 *  digging through Activity. Renders once a QA result exists (job_executed and beyond). */
const QaSummaryPanel: React.FC<{ engagementId: string }> = ({ engagementId }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetQaSummaryQuery(engagementId, { skip: !engagementId });

  if (isLoading || !data || !data.qa_result) return null;

  const passed = data.qa_result === 'pass';

  return (
    <CardSection title={t('jobs.qaSummary', 'QA summary')}>
      <div className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2.5 text-xs">
        {/* Result badge + inspector row */}
        <div className="flex items-center justify-between gap-3">
          <span
            className={
              passed
                ? 'inline-flex items-center gap-1 rounded-full border border-green-300 bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700 dark:border-green-700/40 dark:bg-green-900/20 dark:text-green-400'
                : 'inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700 dark:border-red-700/40 dark:bg-red-900/20 dark:text-red-400'
            }
          >
            {passed ? '✓' : '✕'} {passed ? t('jobs.qaPassed2', 'Passed') : t('jobs.qaFailed2', 'Rework required')}
          </span>
          {data.inspected_by_name && (
            <span className="text-[11px] text-ui-text2 truncate">{data.inspected_by_name}</span>
          )}
          {data.inspected_at && (
            <span className="shrink-0 text-[10px] text-ui-text3">
              {new Date(data.inspected_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </div>

        {!passed && data.reason && (
          <p className="mt-2 text-[11px] text-red-700 dark:text-red-400">
            <span className="font-medium">{t('jobs.defectNotes', 'Defect notes')}: </span>
            {data.reason}
          </p>
        )}
        {data.qa_checklist_task && (
          // The result above is already final — inspect_job/fail_qa_rework's transition click IS
          // the real pass/fail action (v15 decision, see workflow_definition_seed.py's own
          // comments on why a gate requiring THIS task first was tried and reverted: the task is
          // created BY that same transition, so it can't also gate entry into it). This task is
          // the PM's follow-up paperwork/sign-off record, not something still pending on the
          // result itself — worded so "open" doesn't read as "QA isn't really done yet."
          <p className="mt-1.5 text-[10px] text-ui-text3">
            {data.qa_checklist_task.status === 'open'
              ? t('jobs.qaChecklistTaskOpen', 'Follow-up sign-off paperwork still open (PM) — the result above is already final.')
              : t('jobs.qaChecklistTaskDone', 'Follow-up sign-off paperwork completed.')}
          </p>
        )}
      </div>
    </CardSection>
  );
};

export default QaSummaryPanel;
